import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { TRANSIENT_AUTH_PARAMS } from "./sanitizedCurrentUrl";

/**
 * onSigninCallback strips the spent auth params with a raw history.replaceState, which React Router
 * never observes — so its search params stay stale and any setSearchParams((prev) => …) writes them
 * back into the URL. Strip them through the router instead so its location matches the address bar.
 *
 * This is also what removes the `idp` entry marker for a visitor who still has a valid session: the
 * FSW start link is then answered from sessionStorage, so AuthGuard renders straight through and
 * never calls sanitizedCurrentUrl().
 *
 * Call once from each app's root route element (inside RouterProvider). AuthGuard sits outside the
 * router and cannot use router hooks, so the strip cannot live there.
 */
export function useStripAuthCallbackParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!TRANSIENT_AUTH_PARAMS.some((param) => searchParams.has(param))) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        TRANSIENT_AUTH_PARAMS.forEach((param) => next.delete(param));
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);
}
